import React from 'react';
import { CalendarClock, PlayCircle } from 'lucide-react';

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return null;
  }
};

const countdownDays = (iso) => {
  if (!iso) return null;
  try {
    const target = new Date(iso).getTime();
    return Math.ceil((target - Date.now()) / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
};

const SeasonsBlock = ({ show }) => {
  const lastEp = show.last_episode_to_air;
  const nextEp = show.next_episode_to_air;
  const nextIn = countdownDays(nextEp && nextEp.air_date);

  return (
    <section className="block" data-testid="show-seasons">
      <h2 className="block-title">Episodes &amp; Seasons</h2>

      {(nextEp || lastEp) && (
        <div className="ep-row">
          {nextEp ? (
            <div className="ep-card next" data-testid="next-episode-card">
              <div className="ep-icon">
                <CalendarClock size={18} />
              </div>
              <div className="ep-text">
                <span className="ep-eyebrow">Next episode</span>
                <h3>
                  S{nextEp.season_number}·E{nextEp.episode_number}
                  {nextEp.name ? ` — ${nextEp.name}` : ''}
                </h3>
                <p>
                  {nextIn !== null && nextIn > 0
                    ? `Airs in ${nextIn} day${nextIn !== 1 ? 's' : ''}`
                    : 'Airs today'}
                  {' • '}
                  {fmtDate(nextEp.air_date)}
                </p>
              </div>
            </div>
          ) : null}

          {lastEp ? (
            <div className="ep-card last" data-testid="last-episode-card">
              <div className="ep-icon">
                <PlayCircle size={18} />
              </div>
              <div className="ep-text">
                <span className="ep-eyebrow">Latest episode</span>
                <h3>
                  S{lastEp.season_number}·E{lastEp.episode_number}
                  {lastEp.name ? ` — ${lastEp.name}` : ''}
                </h3>
                <p>{fmtDate(lastEp.air_date)}</p>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {show.seasons && show.seasons.length > 0 ? (
        <ul className="season-list" data-testid="season-list">
          {show.seasons.map((s) => (
            <li key={s.id || s.season_number} className="season-row">
              <div className="season-poster">
                {s.poster_url ? (
                  <img src={s.poster_url} alt={`${s.name} poster`} loading="lazy" />
                ) : (
                  <div className="season-poster-fallback">S{s.season_number}</div>
                )}
              </div>
              <div className="season-meta">
                <h4>{s.name}</h4>
                <div className="season-line">
                  {s.episode_count
                    ? `${s.episode_count} episode${s.episode_count !== 1 ? 's' : ''}`
                    : 'TBA'}
                  {s.air_date ? ` • ${fmtDate(s.air_date)}` : ''}
                </div>
                {s.overview ? <p className="season-overview">{s.overview}</p> : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      <style jsx>{`
        .block {
          margin: 2.5rem 0;
        }
        .block-title {
          color: #E62F88;
          font-size: 0.72rem;
          font-weight: 800;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          margin: 0 0 1.25rem;
        }
        .ep-row {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        @media (min-width: 720px) {
          .ep-row {
            grid-template-columns: 1fr 1fr;
          }
        }
        .ep-card {
          display: grid;
          grid-template-columns: 36px 1fr;
          gap: 0.85rem;
          padding: 1rem 1.1rem;
          background: #131313;
          border: 1px solid #242424;
          border-radius: 12px;
        }
        .ep-card.next {
          border-color: rgba(230, 47, 136, 0.35);
          background: linear-gradient(135deg, rgba(230, 47, 136, 0.12) 0%, #131313 60%);
        }
        .ep-icon {
          width: 36px;
          height: 36px;
          border-radius: 999px;
          background: rgba(230, 47, 136, 0.15);
          color: #ff8ec0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .ep-card.last .ep-icon {
          background: rgba(96, 165, 250, 0.12);
          color: #93c5fd;
        }
        .ep-eyebrow {
          color: #a1a1aa;
          font-size: 0.65rem;
          letter-spacing: 0.18em;
          font-weight: 800;
          text-transform: uppercase;
        }
        .ep-card h3 {
          color: #fff;
          font-size: 0.95rem;
          font-weight: 700;
          margin: 0.2rem 0;
        }
        .ep-card p {
          color: #c4c4cc;
          font-size: 0.8rem;
          margin: 0;
        }
        .season-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .season-row {
          display: grid;
          grid-template-columns: 70px 1fr;
          gap: 1rem;
          padding: 0.9rem;
          background: #131313;
          border: 1px solid #232323;
          border-radius: 10px;
        }
        .season-poster {
          aspect-ratio: 2 / 3;
          width: 70px;
          background: #0a0a0a;
          border-radius: 7px;
          overflow: hidden;
        }
        .season-poster img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .season-poster-fallback {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #555;
          font-size: 0.85rem;
        }
        .season-meta h4 {
          color: #fff;
          font-size: 1rem;
          font-weight: 700;
          margin: 0 0 0.2rem;
        }
        .season-line {
          color: #a1a1aa;
          font-size: 0.8rem;
          margin-bottom: 0.4rem;
        }
        .season-overview {
          color: #b4b4b8;
          font-size: 0.85rem;
          line-height: 1.5;
          margin: 0;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </section>
  );
};

export default SeasonsBlock;
